import { Button, Flex, Form, Input, Select } from "antd";
import { useEffect } from "react";

import CustomDrawer from "../../../../../../components/ui/CustomDrawer";
import FileUpload from "../../../../../../components/ui/FileUpload";
import {
  useGetBankListQuery,
  useUpdatePaymentInfoMutation,
} from "../../../../../../features/Vendor/vendor.api";
import useError from "../../../../../../hooks/useError";

import { CustomModalProps } from "../../../../../../types";
import {
  selectArrayOption,
  selectSearchOption,
} from "../../../../../../utils/utils";
import {
  validateBankAccountName,
  validateBDBankAccount,
  validateBDRoutingNumber,
  validateSwiftCode,
} from "../../../../../../utils/validators";
import { ModalData, ModalType } from "../Payments";

type Props = {
  remove: (val: number) => void;
  data: ModalData;
  type: ModalType;
  onFinish: (values: any) => void;
  defaultValues?: any;
} & CustomModalProps;

const ManageBankDrawer = ({
  open,
  remove,
  data,
  type,
  handleClose: onClose,
  onFinish,
  defaultValues,
}: Props) => {
  const [bankForm] = Form.useForm();
  const { data: bankList } = useGetBankListQuery({
    params: { type: "bank", status: "active" },
  });

  const selectedBankName = Form.useWatch(
    ["bankAccount", "bank_name"],
    bankForm,
  );

  const [updateBank, { isLoading, isError, error }] =
    useUpdatePaymentInfoMutation();

  // ---- Helpers ----
  const resetForm = () => bankForm.resetFields();

  const handleFinish = async (values: any) => {
    if (type === "UPDATE-BANK" && defaultValues?.id) {
      const result = await updateBank({
        vendorId: defaultValues?.vendor_id,
        paymentInfoId: defaultValues?.id,
        data: values,
      }).unwrap();

      if (result?.issuccess) {
        onFinish(values);
        resetForm();
      }
    } else {
      onFinish(values);
      resetForm();
    }
  };

  const handleClose = () => {
    if (type === "ADD-BANK") remove(data?.index as number);
    resetForm();
    onClose?.();
  };

  // ---- Effects ----
  useEffect(() => {
    if (type === "UPDATE-BANK") {
      bankForm.setFieldsValue(defaultValues);
    }
  }, [type, bankForm, defaultValues]);

  useEffect(() => {
    if (selectedBankName) {
      const bankData = bankList?.data?.find(
        (bank) => bank.name_en === selectedBankName,
      );
      if (bankData) {
        bankForm.setFieldsValue({
          bankAccount: {
            bank_keyword: bankData.keyword,
            bank_logo: bankData.logo,
          },
        });
      }
    }
  }, [selectedBankName, bankForm, bankList?.data]);

  // ---- Error Handler ----
  useError(isError, error, "Bank update failed.");

  return (
    <CustomDrawer
      title={type === "ADD-BANK" ? "Add Bank" : "Update Bank"}
      open={open}
      onClose={handleClose}
    >
      <Form
        className="custom-form"
        layout="vertical"
        // requiredMark="optional"
        size="large"
        form={bankForm}
        onFinish={handleFinish}
      >
        {/* Hidden Fields */}
        <Form.Item name="payment_type" initialValue="BANK" hidden>
          <Input />
        </Form.Item>
        <Form.Item name={["bankAccount", "bank_logo"]} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={["bankAccount", "bank_keyword"]} hidden>
          <Input />
        </Form.Item>

        {/* Visible Fields */}
        <Form.Item
          className="mb-2"
          label="Bank Name"
          name={["bankAccount", "bank_name"]}
          rules={[{ required: true, message: "Please select bank name" }]}
        >
          <Select
            showSearch
            filterOption={selectSearchOption}
            options={selectArrayOption(
              bankList?.data || [],
              "name_en",
              "name_en",
            )}
            placeholder="Select bank name"
          />
        </Form.Item>

        <Form.Item
          className="mb-2"
          label="Branch Name"
          name={["bankAccount", "branch_name"]}
          rules={[{ required: true, message: "Please enter branch name" }]}
        >
          <Input placeholder="Enter branch name" />
        </Form.Item>

        <Form.Item
          className="mb-2"
          label="Account Name"
          name={["bankAccount", "account_name"]}
          rules={[
            { required: true, message: "Please enter account name" },
            { validator: validateBankAccountName },
          ]}
        >
          <Input placeholder="Enter account name" />
        </Form.Item>

        <Form.Item
          className="mb-2"
          label="Account Number"
          name={["bankAccount", "account_number"]}
          rules={[
            { required: true, message: "Please enter account number" },
            { validator: validateBDBankAccount },
          ]}
        >
          <Input placeholder="Enter account number" />
        </Form.Item>

        <Form.Item
          className="mb-2"
          label="Routing Number"
          name={["bankAccount", "routing_number"]}
          rules={[
            { required: true, message: "Please enter routing number" },
            { validator: validateBDRoutingNumber },
          ]}
        >
          <Input placeholder="Enter routing number" />
        </Form.Item>

        <Form.Item
          className="mb-2"
          label="SWIFT Code"
          name={["bankAccount", "swift_code"]}
          rules={[
            // { required: true, message: "Please enter swift code" },
            { validator: validateSwiftCode },
          ]}
        >
          <Input placeholder="Enter SWIFT code" />
        </Form.Item>

        <Form.Item
          className="mb-2"
          label="Image of Cheque"
          name={["bankAccount", "cheque_image"]}
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
        >
          <FileUpload
            name={["bankAccount", "cheque_image"] as unknown as string}
            form={bankForm}
          />
        </Form.Item>

        {/* Actions */}
        <Flex gap={16} justify="end">
          <Button
            onClick={handleClose}
            size="small"
            className="button-white-bordered"
          >
            Cancel
          </Button>
          <Button
            size="small"
            loading={isLoading}
            htmlType="button"
            className="button-primary min-w-20"
            onClick={() => {
              bankForm.submit();
            }}
          >
            {type === "UPDATE-BANK" ? "Update" : "Add"}
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
};

export default ManageBankDrawer;
